import React, { useCallback, useMemo } from 'react'
import { useQuery } from '@apollo/client'
import classNames from 'classnames'
import { LinkButton } from '@/atoms/Button'
import { Image } from '@/atoms/Image'
import { CaptionSM, LabelSM, TitleLG } from '@/atoms/Text'
import { paths } from '@/constants/paths'
import { Offer } from '@/services/PifService'
import { GuildSale } from '@/services/SaleService'
import { GET_THEATRICAL_RELEASES_FOR_FEATURE } from '@/services/Theatrical/queries'
import {
  GetTheatricalReleasesForFeatureQuery,
  GetTheatricalReleasesForFeatureQueryVariables,
} from '@/types/codegen-federation'
import { getCloudinaryImageUrl } from '@/utils/Cloudinary'
import { useLocale } from '@/utils/LocaleUtil'
import { useSafeTrack } from '@/utils/analytics'
import { writeToSessionStorage } from '@/utils/session-storage'
import { Translate, TranslateFunction, useTranslate } from '@/utils/translate/translate-client'
import { useGuildSignupContext } from '@/views/GuildSignupView/GuildSignupContext'

interface GuildUpsellBannerProps {
  className?: string
  offer: Offer
}

export const GuildUpsellBanner: React.FC<GuildUpsellBannerProps> = ({ className, offer }) => {
  const { t } = useTranslate('guild')
  const { sale } = useGuildSignupContext()
  const { locale } = useLocale()
  const track = useSafeTrack()

  const { data } = useQuery<GetTheatricalReleasesForFeatureQuery, GetTheatricalReleasesForFeatureQueryVariables>(
    GET_THEATRICAL_RELEASES_FOR_FEATURE,
    { variables: { input: { language: locale } }, errorPolicy: 'all' },
  )
  const releaseToPromote = useMemo(() => {
    return data?.theatricalReleases?.theatricalRelease?.find(
      (theatricalRelease) => theatricalRelease?.isFeaturedRelease,
    )
  }, [data?.theatricalReleases?.theatricalRelease])
  const releaseTitle = releaseToPromote?.title
  const releaseDateRaw = releaseToPromote?.releaseDate
  const releaseDate = releaseDateRaw
    ? new Date(releaseDateRaw) < new Date()
      ? t('now', 'now')
      : new Date(releaseDateRaw).toLocaleDateString(locale, { month: 'short', day: 'numeric', timeZone: 'UTC' })
    : undefined

  function getUpsellContent(
    offerId: number,
    t: TranslateFunction,
    releaseTitle?: string | null,
    releaseDate?: string,
    sale?: GuildSale | null,
  ): {
    heading?: string | null
    buttonText?: string | null
    description?: React.ReactNode | null
    href?: string | null
  } {
    switch (offerId) {
      case 1873: {
        const morePerMonth = sale ? 6.39 : 8
        return {
          heading: t('unlockMoreWithPremium', 'Unlock more with Premium!'),
          buttonText: t('goPremium', 'Go Premium'),
          description: (
            <Translate t={t} i18nKey="basicMonthlyDescription" values={{ morePerMonth, releaseTitle, releaseDate }}>
              For just <b>${{ morePerMonth }} more/month</b>, you&apos;ll get <b>ad-free streaming</b> and{' '}
              <b>two movie tickets to every Angel theatrical release</b>, including <i>{{ releaseTitle }}</i> in
              theaters {{ releaseDate }}.
            </Translate>
          ),
          href: `${paths.guild.checkout}/premium-monthly`,
        }
      }
      case 1848: {
        const totalSavings = sale ? 50 : 60
        const yearlyPrice = sale ? 139 : 179
        return {
          heading: t('save50Monthly', 'Save over ${{ totalSavings }} by paying yearly', { totalSavings }),
          buttonText: t('switchToYearly', 'Switch to Yearly'),
          description: (
            <Translate t={t} i18nKey="specialOfferDescription" values={{ totalSavings, yearlyPrice }}>
              Get the same benefits for just <b>${{ yearlyPrice }}/year</b> (That&apos;s over ${{ totalSavings }} less
              than paying monthly).
            </Translate>
          ),
          href: `${paths.guild.checkout}/premium-yearly`,
        }
      }
      case 3169: {
        const morePerYear = sale ? 44 : 60
        return {
          heading: t('unlockMoreWithPremium', 'Unlock more with Premium!'),
          buttonText: t('goPremium', 'Go Premium'),
          description: (
            <Translate t={t} i18nKey="basicYearlyDescriptionV1" values={{ morePerYear, releaseTitle, releaseDate }}>
              For just <b>${{ morePerYear }} more/year</b>, you&apos;ll enjoy <b>ad-free streaming</b> and get{' '}
              <b>two movie tickets to every Angel theatrical release</b> for an entire year, including{' '}
              <i>{{ releaseTitle }}</i> in theaters {{ releaseDate }}.
            </Translate>
          ),
          href: `${paths.guild.checkout}/premium-yearly`,
        }
      }
      default:
        return {
          heading: null,
          buttonText: null,
          description: null,
          href: null,
        }
    }
  }

  const { heading, buttonText, description, href } = getUpsellContent(
    Number(offer.id),
    t,
    releaseTitle,
    releaseDate,
    sale,
  )

  const handleUpsellClick: React.MouseEventHandler<HTMLAnchorElement> = useCallback(
    (e) => {
      e.preventDefault()
      if (!href) return
      track('Guild Checkout Upsell Clicked', {
        offer: offer,
        href: href,
        releaseToPromote: releaseToPromote,
      })
      const checkoutUpsellInitiated = {
        date: new Date().toISOString(),
      }
      writeToSessionStorage('checkoutUpsellInitiated', checkoutUpsellInitiated)
      window.location.href = href
    },
    [href, offer, releaseToPromote, track],
  )

  return (
    <div className="mx-4 mb-8 mt-4 rounded-[10px] bg-guild-gradient p-[2.5px] md:mx-8 lg:mx-16 lg:p-[3px]">
      <div
        className={classNames(
          'p-4 bg-core-gray-900 rounded-lg flex-col lg:flex-row w-full flex lg:justify-between lg:gap-4',
          className,
        )}
      >
        <div className="flex flex-col items-center gap-2 lg:items-start lg:gap-4">
          <TitleLG
            weight="bold"
            className="lg:photon-heading-xs bg-guild-gradient bg-clip-text text-center text-transparent lg:text-left"
          >
            {heading}
          </TitleLG>
          <CaptionSM color="core-gray-400" className=" text-center lg:text-left">
            {description}
          </CaptionSM>
          {releaseToPromote?.images?.angelKeyArt1?.image16x9 && (
            <Image
              src={getCloudinaryImageUrl({
                path: releaseToPromote.images.angelKeyArt1.image16x9,
                width: 250,
                height: 140,
                transforms: 'c_fill',
              })}
              alt={`${releaseToPromote?.title} poster`}
              width={250}
              height={140}
              className="mx-auto my-2 rounded-xl lg:hidden"
            />
          )}
          {href && (
            <LinkButton
              shallow={false}
              prefetch={false}
              href={href}
              onClick={handleUpsellClick}
              variant="gray-700"
              className="w-full border-none bg-black/20 px-5 py-[11px] backdrop-blur md:w-fit"
            >
              <LabelSM color="white">{buttonText}</LabelSM>
            </LinkButton>
          )}
        </div>
        <div className="lg:min-w-[237px]">
          {releaseToPromote?.images?.angelKeyArt1?.image16x9 && (
            <Image
              src={getCloudinaryImageUrl({
                path: releaseToPromote.images.angelKeyArt1.image16x9,
                width: 237,
                height: 132,
                transforms: 'c_fill',
              })}
              alt={`${releaseToPromote?.title} poster`}
              width={237}
              height={132}
              className="hidden rounded-xl lg:flex"
            />
          )}
        </div>
      </div>
    </div>
  )
}
